import apiClient from '@/lib/axios';
import { Agent } from '@/services/types/user';

export const agentsApi = {
  // Get all agents
  getAgents: async (params: any = {}): Promise<{ agents: Agent[]; pagination: any }> => {
    const response = await apiClient.get('/api/agents', { params });
    // Backend returns { success, data, pagination }, frontend expects { agents, pagination }
    // Also map _id to id for frontend compatibility
    const agents = response.data.data.map((agent: any) => ({
      ...agent,
      id: agent._id,
    }));
    return {
      agents,
      pagination: response.data.pagination,
    };
  },

  // Get agent by ID
  getAgentById: async (id: string): Promise<Agent> => {
    const response = await apiClient.get(`/api/agents/${id}`);
    // Backend returns { success, data }, frontend expects Agent directly
    // Also map _id to id for frontend compatibility
    return {
      ...response.data.data,
      id: response.data.data._id,
    };
  },

  // Get agent dashboard data (not implemented in backend yet)
  getAgentDashboard: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/api/agents/${id}/dashboard`);
    return response.data;
  },

  // Update agent profile (not implemented in backend yet)
  updateAgentProfile: async (id: string, profileData: Partial<Agent>): Promise<Agent> => {
    const response = await apiClient.put(`/api/agents/${id}`, profileData);
    return response.data;
  },

  // Get agent availability (not implemented in backend yet)
  getAgentAvailability: async (id: string): Promise<any> => {
    const response = await apiClient.get(`/api/agents/${id}/availability`);
    return response.data;
  },

  // Update agent availability (not implemented in backend yet)
  updateAgentAvailability: async (id: string, availability: any): Promise<any> => {
    const response = await apiClient.put(`/api/agents/${id}/availability`, availability);
    return response.data;
  },

  // Get agent leads from agent-assignment endpoint
  getAgentLeads: async (): Promise<any> => {
    const response = await apiClient.get('/api/agent-assignment/leads');
    return response.data;
  },

  // Get agent bookings (not implemented in backend yet)
  getAgentBookings: async (id: string, params: any = {}): Promise<any> => {
    const response = await apiClient.get(`/api/agents/${id}/bookings`, { params });
    return response.data;
  },

  // Get agent reviews (not implemented in backend yet)
  getAgentReviews: async (id: string, params: any = {}): Promise<any> => {
    const response = await apiClient.get(`/api/agents/${id}/reviews`, { params });
    return response.data;
  },

  // Add agent review (not implemented in backend yet)
  addAgentReview: async (id: string, reviewData: any): Promise<any> => {
    const response = await apiClient.post(`/api/agents/${id}/reviews`, reviewData);
    return response.data;
  },

  // Get top agents
  getTopAgents: async (limit: number = 10): Promise<Agent[]> => {
    const response = await apiClient.get('/api/agents/top', { params: { limit } });
    // Backend returns { success, data }, frontend expects Agent[] directly
    // Also map _id to id for frontend compatibility
    return response.data.data.map((agent: any) => ({
      ...agent,
      id: agent._id,
    }));
  },

  // Search agents
  searchAgents: async (query: string, params: any = {}): Promise<{ agents: Agent[]; pagination: any }> => {
    const response = await apiClient.get('/api/agents/search', { 
      params: { q: query, ...params } 
    });
    // Backend returns { success, data, pagination }, frontend expects { agents, pagination }
    // Also map _id to id for frontend compatibility
    const agents = response.data.data.map((agent: any) => ({
      ...agent,
      id: agent._id,
    }));
    return {
      agents,
      pagination: response.data.pagination,
    };
  },

  // Get assigned agent for current customer
  getAssignedAgent: async (): Promise<Agent | null> => {
    try {
      const response = await apiClient.get('/api/customers/assigned-agent');
      return response.data.agent || null;
    } catch (error) {
      return null;
    }
  },
};
